import React, { useState, useEffect } from 'react'
import {
  Typography,
  Card,
  Row,
  Col,
  Button,
  message,
  Spin,
  Tag,
  List,
  Progress,
  Statistic,
  Space,
  Table,
  Tooltip,
  Alert,
  Collapse,
} from 'antd'
import {
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  HeartOutlined,
  DatabaseOutlined,
  CloudServerOutlined,
  HddOutlined,
  ApiOutlined,
  ThunderboltOutlined,
  HistoryOutlined,
} from '@ant-design/icons'
import axios from 'axios'

const { Title, Text } = Typography
const { Panel } = Collapse

const HealthCheck = () => {
  const [healthData, setHealthData] = useState(null)
  const [historyData, setHistoryData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [lastRefresh, setLastRefresh] = useState(null)

  useEffect(() => {
    fetchHealthCheck()
    fetchHistory()
  }, [])

  const fetchHealthCheck = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get('/system/health-check/full', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setHealthData(response.data)
      setLastRefresh(new Date())
    } catch (error) {
      console.error('Failed to fetch health check:', error)
      message.error('Failed to load health check data')
    } finally {
      setLoading(false)
    }
  }

  const fetchHistory = async () => {
    setHistoryLoading(true)
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get('/system/health-check/history?days=7', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setHistoryData(response.data)
    } catch (error) {
      console.error('Failed to fetch health history:', error)
    } finally {
      setHistoryLoading(false)
    }
  }

  const getStatusIcon = (status, size = 20) => {
    const style = { fontSize: `${size}px` }
    if (status === 'healthy' || status === true) {
      return <CheckCircleOutlined style={{ ...style, color: '#52c41a' }} />
    }
    if (status === 'critical' || status === false) {
      return <CloseCircleOutlined style={{ ...style, color: '#f5222d' }} />
    }
    return <WarningOutlined style={{ ...style, color: '#faad14' }} />
  }

  const getStatusTag = (status) => {
    if (status === 'healthy' || status === true) {
      return <Tag color="green">HEALTHY</Tag>
    }
    if (status === 'critical' || status === false) {
      return <Tag color="red">CRITICAL</Tag>
    }
    return <Tag color="orange">WARNING</Tag>
  }

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'database':
        return <DatabaseOutlined />
      case 'service':
        return <CloudServerOutlined />
      case 'system':
        return <HddOutlined />
      case 'api':
        return <ApiOutlined />
      case 'pipeline':
        return <ThunderboltOutlined />
      default:
        return <HeartOutlined />
    }
  }

  const groupChecksByCategory = (checks) => {
    const grouped = {}
    checks?.forEach(check => {
      if (!grouped[check.category]) {
        grouped[check.category] = []
      }
      grouped[check.category].push(check)
    })
    return grouped
  }

  const historyColumns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Pipeline Runs',
      dataIndex: 'total_runs',
      key: 'total_runs',
    },
    {
      title: 'Success Rate',
      dataIndex: 'success_rate',
      key: 'success_rate',
      render: (rate) => (
        <Progress
          percent={rate}
          size="small"
          status={rate >= 95 ? 'success' : rate >= 80 ? 'normal' : 'exception'}
          format={(percent) => `${percent}%`}
        />
      ),
    },
    {
      title: 'Failed',
      dataIndex: 'failed',
      key: 'failed',
      render: (failed) => (
        <Text type={failed > 0 ? 'danger' : 'secondary'}>{failed}</Text>
      ),
    },
    {
      title: 'Avg Duration',
      dataIndex: 'avg_duration_seconds',
      key: 'avg_duration_seconds',
      render: (duration) => duration ? `${duration}s` : '-',
    },
  ]

  const groupedChecks = groupChecksByCategory(healthData?.checks)

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <Title level={2}>
          <HeartOutlined className="mr-2" />
          System Health Check
        </Title>
        <Space>
          {lastRefresh && (
            <Text type="secondary">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </Text>
          )}
          <Button
            type="primary"
            icon={<ReloadOutlined spin={loading} />}
            onClick={fetchHealthCheck}
            loading={loading}
          >
            Run Health Check
          </Button>
        </Space>
      </div>

      {/* Overall Status Banner */}
      {healthData && (
        <Alert
          message={
            <Space>
              {getStatusIcon(healthData.status, 24)}
              <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
                System Status: {healthData.status?.toUpperCase()}
              </span>
            </Space>
          }
          description={
            healthData.status === 'healthy'
              ? 'All systems are operating normally.'
              : healthData.status === 'warning'
              ? `${healthData.warnings?.length || 0} warning(s) detected. Review below for details.`
              : `${healthData.errors?.length || 0} critical issue(s) detected! Immediate attention required.`
          }
          type={healthData.status === 'healthy' ? 'success' : healthData.status === 'warning' ? 'warning' : 'error'}
          showIcon={false}
          className="mb-6"
        />
      )}

      {/* Summary Statistics */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Total Checks"
              value={healthData?.summary?.total_checks || 0}
              prefix={<HeartOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Passed"
              value={healthData?.summary?.passed || 0}
              valueStyle={{ color: '#3f8600' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Warnings"
              value={healthData?.summary?.warnings || 0}
              valueStyle={{ color: '#faad14' }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Critical"
              value={healthData?.summary?.critical || 0}
              valueStyle={{ color: '#cf1322' }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Health Check Details */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card
            title={
              <Space>
                <HeartOutlined />
                Health Check Details
              </Space>
            }
            loading={loading}
          >
            {healthData?.checks ? (
              <Collapse defaultActiveKey={Object.keys(groupedChecks)}>
                {Object.entries(groupedChecks).map(([category, checks]) => {
                  const passedCount = checks.filter(c => c.passed).length
                  const totalCount = checks.length
                  const allPassed = passedCount === totalCount

                  return (
                    <Panel
                      key={category}
                      header={
                        <Space>
                          {getCategoryIcon(category)}
                          <span style={{ textTransform: 'capitalize' }}>{category}</span>
                          <Tag color={allPassed ? 'green' : 'orange'}>
                            {passedCount}/{totalCount}
                          </Tag>
                        </Space>
                      }
                    >
                      <List
                        dataSource={checks}
                        renderItem={(check) => (
                          <List.Item>
                            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                              <Space>
                                {getStatusIcon(check.passed)}
                                <Text strong>{check.name}</Text>
                                {check.critical && !check.passed && (
                                  <Tag color="red">CRITICAL</Tag>
                                )}
                              </Space>
                              <Text type="secondary">{check.details}</Text>
                            </Space>
                          </List.Item>
                        )}
                      />
                    </Panel>
                  )
                })}
              </Collapse>
            ) : (
              <div className="text-center py-8">
                <Spin size="large" />
                <div className="mt-4">
                  <Text type="secondary">Loading health check data...</Text>
                </div>
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          {/* Warnings Card */}
          {healthData?.warnings?.length > 0 && (
            <Card
              title={
                <Space>
                  <WarningOutlined style={{ color: '#faad14' }} />
                  Warnings
                </Space>
              }
              className="mb-4"
            >
              <List
                dataSource={healthData.warnings}
                renderItem={(warning) => (
                  <List.Item>
                    <Text type="warning">{warning}</Text>
                  </List.Item>
                )}
              />
            </Card>
          )}

          {/* Errors Card */}
          {healthData?.errors?.length > 0 && (
            <Card
              title={
                <Space>
                  <CloseCircleOutlined style={{ color: '#f5222d' }} />
                  Critical Errors
                </Space>
              }
              className="mb-4"
            >
              <List
                dataSource={healthData.errors}
                renderItem={(error) => (
                  <List.Item>
                    <Text type="danger">{error}</Text>
                  </List.Item>
                )}
              />
            </Card>
          )}

          {/* Quick Info Card */}
          <Card
            title={
              <Space>
                <HistoryOutlined />
                Quick Info
              </Space>
            }
          >
            <div className="space-y-2">
              <div>
                <Text type="secondary">Timestamp: </Text>
                <Text>
                  {healthData?.timestamp
                    ? new Date(healthData.timestamp).toLocaleString()
                    : '-'}
                </Text>
              </div>
              <div>
                <Text type="secondary">Health Check Cron: </Text>
                <Text>Daily at 7:00 AM ET</Text>
              </div>
              <div>
                <Text type="secondary">Log Location: </Text>
                <Text code>~/acis-ai-platform/logs/health_check.log</Text>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* History Table */}
      <Card
        title={
          <Space>
            <HistoryOutlined />
            Pipeline Health History (Last 7 Days)
          </Space>
        }
        className="mt-6"
        loading={historyLoading}
      >
        <Table
          dataSource={historyData?.history || []}
          columns={historyColumns}
          rowKey="date"
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  )
}

export default HealthCheck
